/**
 * Google Tag Manager integration.
 *
 * Provides the GTM snippet for injection into the page head,
 * the noscript fallback for the body, and consent default setup.
 *
 * In Vite: injected via index.html placeholders.
 * In Next.js: injected via next/script in app/layout.tsx.
 */

import { getStoredConsent, hasMarketingConsent } from '@/lib/cookieConsent';
import { getAnalyticsConfig } from './config';

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Set Consent Mode v2 defaults before GTM loads.
 * Must be called synchronously before the GTM snippet executes.
 */
export function setConsentDefaults(): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as Record<string, unknown>);
  };

  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    wait_for_update: 500,
  });
}

/**
 * Update consent state when user grants/denies categories.
 */
export function updateConsent(grants: {
  analytics_storage?: 'granted' | 'denied';
  ad_storage?: 'granted' | 'denied';
  ad_user_data?: 'granted' | 'denied';
  ad_personalization?: 'granted' | 'denied';
  functionality_storage?: 'granted' | 'denied';
  personalization_storage?: 'granted' | 'denied';
}): void {
  window.gtag?.('consent', 'update', grants);
}

/**
 * If the user already saved consent (e.g. returning visit or E2E prefill), apply it
 * **before** GTM loads so Consent Mode matches localStorage when tags first fire.
 * Mirrors `useCookieConsent` → `syncConsentToGTM`.
 */
export function applyStoredConsentBeforeGtmLoad(): void {
  const stored = getStoredConsent();
  if (!stored) return;
  updateConsent({
    analytics_storage: stored.analytics ? 'granted' : 'denied',
    ad_storage: stored.marketing ? 'granted' : 'denied',
    ad_user_data: stored.marketing ? 'granted' : 'denied',
    ad_personalization: stored.marketing ? 'granted' : 'denied',
    functionality_storage: 'granted',
    personalization_storage: stored.personalization ? 'granted' : 'denied',
  });
}

/**
 * Load the GTM container script.
 * Call this AFTER setConsentDefaults().
 */
export function loadGTM(): void {
  const config = getAnalyticsConfig();
  if (!config.gtmId) return;

  const w = window;
  const d = document;
  const l = 'dataLayer';
  const i = config.gtmId;

  w[l] = w[l] || [];
  w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const f = d.getElementsByTagName('script')[0];
  const j = d.createElement('script');
  j.async = true;

  let src = `https://www.googletagmanager.com/gtm.js?id=${i}`;
  if (config.gtmAuth && config.gtmPreview) {
    src += `&gtm_auth=${config.gtmAuth}&gtm_preview=${config.gtmPreview}&gtm_cookies_win=x`;
  }
  j.src = src;

  f?.parentNode?.insertBefore(j, f);
}

/**
 * Push an event to the GTM dataLayer.
 */
export function pushToDataLayer(eventName: string, properties: Record<string, unknown>): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...properties,
  });
}

/**
 * Push Meta Pixel `CompleteRegistration` onto the dataLayer so a GTM tag in
 * `GTM-P3MFTP72` can fire `fbq('track','CompleteRegistration')`.
 *
 * Consent-gated on Marketing / `ad_storage` (signup-implied consent is analytics-only since REK-3389; needs banner Accept or the signup marketing checkbox).
 * Does **not** load the Pixel ID in app code — Pixel stays GTM-only (REK-2146).
 *
 * Prefer this explicit conversion push over re-routing all `signup_completed`
 * PostHog traffic through GA4/`dataLayer`.
 */
export function pushCompleteRegistration(
  properties: Record<string, unknown> = {},
): void {
  if (typeof window === 'undefined') return;
  if (!hasMarketingConsent()) return;
  pushToDataLayer('CompleteRegistration', properties);
}

/**
 * Get the GTM noscript iframe HTML for the body fallback.
 */
export function getGTMNoscriptHTML(): string {
  const config = getAnalyticsConfig();
  if (!config.gtmId) return '';
  return `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
}
// GTM env auth tokens removed - using Live container for all environments
